/**
 * LunchNudgeBanner — iter628 — Polls /api/lunch-nudges/pending every 60s.
 * If a nudge is found, shows a friendly Bulgarian banner + Web Push (if permission granted).
 * Auto-dismisses when user logs a meal (listens to meal-logged/meal-added events).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Apple, Bell, X } from "lucide-react";

export default function LunchNudgeBanner() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [nudge, setNudge] = useState(null);
  const [permissionRequested, setPermissionRequested] = useState(false);

  const fetchPending = async () => {
    try {
      const { data } = await axios.get(`${API}/lunch-nudges/pending`, { timeout: 8000 });
      const item = (data.items || [])[0] || null;
      setNudge(item);
      if (item && "Notification" in window && Notification.permission === "granted") {
        // Show OS-level notification
        try {
          const n = new Notification(item.title_bg, {
            body: item.message_bg,
            icon: "/favicon.ico",
            tag: `lunch-nudge-${item.id}`,
            requireInteraction: false,
          });
          n.onclick = () => {
            window.focus();
            navigate(item.cta_route || "/dashboard/plate-simulator");
          };
        } catch { /* OS denied silently */ }
      }
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchPending();
    const t = setInterval(fetchPending, 60 * 1000);
    const onMeal = () => { setNudge(null); fetchPending(); };
    window.addEventListener("meal-logged", onMeal);
    window.addEventListener("meal-added", onMeal);
    window.addEventListener("meal-log-changed", onMeal);
    return () => {
      clearInterval(t);
      window.removeEventListener("meal-logged", onMeal);
      window.removeEventListener("meal-added", onMeal);
      window.removeEventListener("meal-log-changed", onMeal);
    };
    // eslint-disable-next-line
  }, [API]);

  const requestPermission = async () => {
    if (!("Notification" in window)) return;
    setPermissionRequested(true);
    try {
      await Notification.requestPermission();
    } catch { /* */ }
  };

  const dismiss = async () => {
    if (!nudge) return;
    try {
      await axios.post(`${API}/lunch-nudges/ack`, { nudge_id: nudge.id });
    } catch { /* */ }
    setNudge(null);
  };

  if (!nudge) {
    // First-time: if permission unknown and no nudge, gently nudge for permission once
    if ("Notification" in window && Notification.permission === "default" && !permissionRequested) {
      return (
        <div className={`p-2.5 rounded-xl mb-3 flex items-center gap-2 ${isDark ? "bg-slate-900/40 border border-slate-700" : "bg-slate-50 border border-slate-200"}`} data-testid="nudge-permission-prompt">
          <Bell className="w-4 h-4 text-indigo-500 shrink-0" />
          <div className="text-[11px] flex-1">
            Включи напомняне в 14:00 ч., ако не си логнал обяд — мозъкът ти ще ти благодари.
          </div>
          <button onClick={requestPermission} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="nudge-enable-btn">
            Включи
          </button>
        </div>
      );
    }
    return null;
  }

  return (
    <div
      className={`p-3 rounded-xl mb-3 border-2 ${isDark ? "bg-amber-900/30 border-amber-500/50" : "bg-amber-50 border-amber-300"}`}
      data-testid="lunch-nudge-banner"
    >
      <div className="flex items-start gap-3">
        <Apple className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className={`text-[11px] font-bold uppercase tracking-wider ${isDark ? "text-amber-300" : "text-amber-700"}`} data-testid="lunch-nudge-title">
            🔔 {nudge.title_bg}
          </div>
          <p className="text-xs sm:text-sm mt-0.5 leading-relaxed" data-testid="lunch-nudge-message">{nudge.message_bg}</p>
        </div>
        <button
          onClick={() => { navigate(nudge.cta_route || "/dashboard/plate-simulator"); dismiss(); }}
          className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white transition-colors"
          data-testid="lunch-nudge-cta-btn"
        >
          {nudge.cta_bg || "Логни хапка"}
        </button>
        <button
          onClick={dismiss}
          className="shrink-0 p-1 rounded-md opacity-50 hover:opacity-100 transition-opacity"
          data-testid="lunch-nudge-dismiss-btn"
          aria-label="Затвори"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
